import { Link } from 'react-router-dom'
import { formatAgeMs } from '../format.js'
import { formatAgo, heartbeatStatus, opsCheckLabel } from '../health/ops-health.js'
import { useHealthDashboard, useOpsHealth } from '../health/queries.js'
import { summarizeTurnaround } from '../health/speed.js'
import { useVerdicts, verdictLabel } from '../reference/queries.js'
import { useRuns } from '../runs/queries.js'
import { AsOfLine, HairlineRule, Skeleton, TrendChart } from '../ui/index.js'
import styles from './HealthPage.module.css'

/** One decimal — the input can carry a second (`69423baa` fixtures do), and a claimed precision this fine invents nothing the wire didn't already state. */
function formatHours(value: number): string {
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} hours`
}

function formatDays(value: number): string {
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} days`
}

/**
 * `LGH-029` §2. `PLT-054` serves `turnaround` per case and explicitly refuses
 * to compute a tenant-level average itself — this composes one client-side,
 * per its own REQ's option (2), from a sample the console already has a path
 * to fetch. `RunListQuery.limit` caps at 100 (`cadence.ts`), the same
 * pagination surface every other list consumer already hits — checked with
 * platform core before choosing this over a served distribution.
 */
const SPEED_SAMPLE_SIZE = 100

function SpeedSection() {
  const recent = useRuns({
    filter: {},
    sort: 'time',
    sortDirection: 'desc',
    offset: 0,
    limit: SPEED_SAMPLE_SIZE,
  })

  return (
    <section>
      <h2 className={styles['sectionTitle']}>Speed</h2>
      <p className={styles['sectionSubtitle']}>
        How long a customer waits, start to finish — the last {SPEED_SAMPLE_SIZE} runs, not a
        tenant-wide average.
      </p>
      {recent.isPending && <Skeleton height="4rem" />}
      {recent.isError && (
        <p role="alert" className={styles['error']}>
          Could not load recent runs. {recent.error.message}
        </p>
      )}
      {recent.data &&
        (() => {
          const summary = summarizeTurnaround(recent.data.rows)
          if (summary.measuredCount === 0) {
            return (
              <p className={styles['unavailable']}>
                No run in this sample has a measurable turnaround yet.
              </p>
            )
          }
          return (
            <dl className={styles['detailList']}>
              {summary.hours && (
                <div className={styles['detailRow']}>
                  <dt>Median turnaround (hours-precision runs)</dt>
                  <dd>
                    {formatHours(summary.hours.median)} · {summary.hours.count}/{summary.sampleSize}{' '}
                    runs
                  </dd>
                </div>
              )}
              {/*
                No producer today (`TurnaroundReading`'s own header) — this
                branch is here so a `days`-precision run, the day one exists,
                renders as its own honest median rather than being silently
                dropped or invented into an hours figure it never claimed.
              */}
              {summary.days && (
                <div className={styles['detailRow']}>
                  <dt>Median turnaround (days-precision runs)</dt>
                  <dd>
                    {formatDays(summary.days.median)} · {summary.days.count}/{summary.sampleSize}{' '}
                    runs
                  </dd>
                </div>
              )}
              {summary.measuredCount < summary.sampleSize && (
                <div className={styles['detailRow']}>
                  <dt>Not yet measurable</dt>
                  <dd>
                    {summary.sampleSize - summary.measuredCount}/{summary.sampleSize} runs
                  </dd>
                </div>
              )}
            </dl>
          )
        })()}
    </section>
  )
}

/**
 * OPS-013. The production box's own host checks, written every 5 minutes by
 * its cron healthcheck into this console's database. Rendered independently of
 * the Cadence-backed dashboard below: if Cadence is down, this still shows.
 *
 * "No open problems" is only said when the heartbeat is fresh. A stale or
 * missing heartbeat means the healthcheck itself may have stopped, and an
 * empty list then proves nothing, so the screen says that instead.
 */
function OpsHealthSection() {
  const ops = useOpsHealth()

  return (
    <section>
      <h2 className={styles['sectionTitle']}>Server checks</h2>
      <p className={styles['sectionSubtitle']}>
        The production server&apos;s own checks — inbound mail, database backups, disk. Run every 5
        minutes.
      </p>
      {ops.isPending && <Skeleton height="3rem" />}
      {ops.isError && (
        <p role="alert" className={styles['error']}>
          Could not load server checks. {ops.error.message}
        </p>
      )}
      {ops.data &&
        (() => {
          const now = Date.now()
          const { heartbeat, openAlerts, recentlyResolved } = ops.data
          const status = heartbeatStatus(heartbeat, now)
          return (
            <>
              {status === 'never' && (
                <p role="alert" className={styles['error']}>
                  The server healthcheck has never reported, so nothing here can be read as all
                  clear.
                </p>
              )}
              {status === 'stale' && heartbeat && (
                <p role="alert" className={styles['error']}>
                  The server healthcheck last reported{' '}
                  {formatAgo(now - Date.parse(heartbeat.lastRunAt))}. It may have stopped, so the
                  list below may be out of date.
                </p>
              )}
              {status === 'fresh' && heartbeat && (
                <p className={styles['unavailable']}>
                  Last checked {formatAgo(now - Date.parse(heartbeat.lastRunAt))}.
                  {heartbeat.notice !== null && ` Note: ${heartbeat.notice}.`}
                </p>
              )}

              {openAlerts.length === 0 ? (
                status === 'fresh' && <p className={styles['unavailable']}>No open problems.</p>
              ) : (
                <dl className={styles['detailList']}>
                  {openAlerts.map((alert) => (
                    <div key={alert.id} className={styles['detailRow']}>
                      <dt>{opsCheckLabel(alert.checkKey)}</dt>
                      <dd>
                        {alert.message} · since {formatAgo(now - Date.parse(alert.firstSeenAt))}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}

              {recentlyResolved.length > 0 && (
                <>
                  <p className={styles['sectionSubtitle']}>Resolved in the last 7 days</p>
                  <ul className={styles['subList']}>
                    {recentlyResolved.map((alert) => (
                      <li key={alert.id}>
                        {opsCheckLabel(alert.checkKey)} — resolved{' '}
                        {alert.resolvedAt !== null && formatAgo(now - Date.parse(alert.resolvedAt))}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )
        })()}
    </section>
  )
}

/**
 * Exception classes are NOT verdicts, and only some of them overlap.
 *
 * `ambiguity` and `rule_needed` are attention reasons the console names, and
 * Cadence publishes no vocabulary for them — so they stay here, which is
 * honest rather than lazy. Anything that IS a verdict is looked up instead,
 * because that vocabulary is served and retyping it is the drift CON-003
 * exists to prevent.
 */
const NON_VERDICT_EXCEPTION_LABEL: Record<string, string> = {
  ambiguity: 'Customer identity ambiguous',
  rule_needed: 'Item not in your catalogue',
}

/**
 * Operator-console UX audit finding. This comment used to claim *"Cadence
 * returns `doesnt_match` in this field, which the mapping layer has already
 * turned into the console's word"* — checked directly against the live
 * response rather than trusted: `exceptionsByClass` genuinely carries
 * `"doesnt_match"`, unmapped, and `/reference/verdicts` serves the same
 * concept as `"mismatch"`. The claimed translation does not exist, so this
 * screen rendered the raw internal string next to two properly humanised
 * siblings in the same list.
 *
 * Fixed here as a KEY translation, not a new hardcoded label — `mismatch`
 * already has a served label (`verdictLabel` below still reads it), so
 * inventing a second literal string for the same concept would be exactly
 * the drift `CON-003` exists to prevent, just one file over from where it
 * usually shows up. If Cadence's own mapping layer is later fixed to emit
 * `mismatch` directly, this becomes a no-op and can be deleted.
 */
const EXCEPTION_CLASS_TO_VERDICT: Record<string, string> = {
  doesnt_match: 'mismatch',
}

/**
 * Operator-console UX audit finding, wire-verified against
 * `/api/v1/health/dashboard`: `autonomyPct`/`verifiedPct` are served as
 * fractions of 1 (`0.69`, not `69`) — `healthTrendsSchema`'s own name is the
 * part that's misleading, not the value. This function stringified the raw
 * fraction with a bare `%` appended, so the screen read "0.69%" where every
 * sibling reading of the same underlying ratio (this page's own
 * `autonomyDetail`/`verificationDetail` counts, and Home's metric cards)
 * correctly shows "69%".
 */
function pct(value: number): string {
  return `${String(Math.round(value * 100))}%`
}

function count(value: number): string {
  return String(value)
}

/**
 * UI spec §6. Weekly review — deliberately not the landing page, and
 * deliberately not the fixed-viewport, no-scroll layout Home uses (UI spec
 * §2): a review surface is read top to bottom, not glanced at once.
 */
export function HealthPage() {
  const health = useHealthDashboard()
  const verdicts = useVerdicts()
  const data = health.data

  return (
    <main className={styles['page']}>
      <Link to="/" className={styles['home']}>
        ← Home
      </Link>

      <div className={styles['sectionHeader']}>
        <div>
          <h1 className={styles['pageTitle']}>Health</h1>
          <p className={styles['subtitle']}>Weekly review — not where daily work happens.</p>
        </div>
        {data && <AsOfLine asOf={data.asOf} onRefresh={() => void health.refetch()} />}
      </div>

      <OpsHealthSection />

      <HairlineRule strong />

      {health.isPending && (
        <div className={styles['trends']}>
          <Skeleton height="6rem" />
          <Skeleton height="6rem" />
          <Skeleton height="6rem" />
          <Skeleton height="6rem" />
        </div>
      )}

      {health.isError && (
        <p role="alert" className={styles['error']}>
          Could not load Health data. {health.error.message}
        </p>
      )}

      {data && (
        <>
          <section className={styles['trends']}>
            <TrendChart label="Runs / week" points={data.trends.runsPerWeek} formatValue={count} />
            <TrendChart label="Autonomy" points={data.trends.autonomyPct} formatValue={pct} />
            <TrendChart
              label="Attention arising"
              points={data.trends.attentionOpenCount}
              formatValue={count}
            />
            <TrendChart label="Verified" points={data.trends.verifiedPct} formatValue={pct} />
          </section>

          <HairlineRule strong />

          <section>
            <h2 className={styles['sectionTitle']}>Autonomy detail</h2>
            <p className={styles['sectionSubtitle']}>What took operator time this week.</p>
            <dl className={styles['detailList']}>
              <div className={styles['detailRow']}>
                <dt>Fully autonomous</dt>
                <dd>
                  {data.autonomyDetail.autonomousCount}/{data.autonomyDetail.totalCount}
                </dd>
              </div>
              <div className={styles['detailRow']}>
                <dt>Required approval — above your threshold</dt>
                <dd>
                  {data.autonomyDetail.requiresApprovalCount}/{data.autonomyDetail.totalCount}
                </dd>
              </div>
              <div className={styles['detailRow']}>
                <dt>Hit an exception</dt>
                <dd>
                  {data.autonomyDetail.exceptionCount}/{data.autonomyDetail.totalCount}
                </dd>
              </div>
            </dl>
            {data.autonomyDetail.exceptionsByClass.length > 0 && (
              <ul className={styles['subList']}>
                {data.autonomyDetail.exceptionsByClass.map((e) => (
                  <li key={e.class}>
                    {/* A verdict if the vocabulary knows it — translating the
                        exception-class key to the verdict key first where the
                        two are known to name the same concept
                        (`EXCEPTION_CLASS_TO_VERDICT`) — otherwise a
                        console-named reason; otherwise the raw class, which
                        is the platform's own word and better than a guess. */}
                    {NON_VERDICT_EXCEPTION_LABEL[e.class] ??
                      verdictLabel(
                        verdicts.data,
                        EXCEPTION_CLASS_TO_VERDICT[e.class] ?? e.class,
                      )}{' '}
                    — {e.count}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <HairlineRule />

          <section>
            <h2 className={styles['sectionTitle']}>Verification detail</h2>
            <p className={styles['sectionSubtitle']}>What verification found this week.</p>
            <dl className={styles['detailList']}>
              <div className={styles['detailRow']}>
                <dt>{verdictLabel(verdicts.data, 'confirmed')}</dt>
                <dd>
                  {data.verificationDetail.confirmedCount}/{data.verificationDetail.totalCount}
                </dd>
              </div>
              <div className={styles['detailRow']}>
                <dt>
                  {verdictLabel(verdicts.data, 'mismatch')}
                  {data.verificationDetail.mismatchTopField !== null &&
                    ` — most often disagrees on: ${data.verificationDetail.mismatchTopField}`}
                </dt>
                <dd>
                  {data.verificationDetail.mismatchCount}/{data.verificationDetail.totalCount}
                </dd>
              </div>
              <div className={styles['detailRow']}>
                <dt>{verdictLabel(verdicts.data, 'not_confirmed_yet')}</dt>
                <dd>{data.verificationDetail.notConfirmedYetCount}</dd>
              </div>
            </dl>
          </section>

          <HairlineRule />

          {/* An empty list here is Cadence's answer, not the console's
              silence: it looked and found none. That is why this says so
              plainly rather than hiding the section. */}
          <section>
            <h2 className={styles['sectionTitle']}>Policy gaps</h2>
            {data.policyGaps.gaps.length === 0 ? (
              <p className={styles['unavailable']}>No policy gaps recorded in this period.</p>
            ) : (
              <dl className={styles['detailList']}>
                {data.policyGaps.gaps.map((gap) => (
                  <div key={gap.pattern}>
                    <dt>{gap.pattern}</dt>
                    <dd>
                      {gap.count} {gap.count === 1 ? 'run' : 'runs'} · most recent{' '}
                      {formatAgeMs(Date.now() - Date.parse(gap.mostRecent))} ago
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </section>

          <HairlineRule />

          <SpeedSection />
        </>
      )}
    </main>
  )
}
